/**
 * Key takeaways shown in the collapsible "Summary" box at the top of each
 * article (components/ArticleSummary). Keyed by the article's route segment
 * (/articles/<id>). Every point restates what the article itself says —
 * nothing here goes beyond it.
 *
 * English lives here; translations sit in the locale files as
 * "artSum.<id>.<index>" and fall back to English when missing.
 */
const articleSummaries = {
  aiProjectBrief: [
    'A one-line brief gets quotes that differ wildly, because each vendor guesses what you mean and prices in the risk.',
    'Describe the problem and its cost, not the technology you think you need.',
    'Be specific about your data, a measurable success criterion, your constraints and a budget range.',
    'Good vendors answer with questions and a range with assumptions; be wary of fixed prices or accuracy promises before anyone has seen your data.',
  ],
  chatbotVsAgent: [
    'A chatbot returns an answer for a person to act on; an agent uses tools to take actions in your systems.',
    'Chatbots fit question-answering and are the safer first project; agents fit repetitive, rule-based workflows.',
    'When AI can act, limits matter: minimal permissions, approvals for irreversible steps, audit logs and escalation.',
    'Start an agent read-only with human approval, measure acceptance, and remove approvals gradually on low-risk steps.',
  ],
  measureAiAssistant: [
    'Fluent answers are not proof of quality; measure the assistant on questions whose answers you already know.',
    'Build a test set of 50–150 real questions, including hard cases and questions the documents cannot answer.',
    'Measure retrieval, correctness, faithfulness, citations and refusals — plus response time and cost per answer.',
    'Group failures by cause, re-run the test set after every change, and set the quality bar by what a wrong answer costs.',
  ],
  aiDevelopmentCost: [
    'A focused first version of most AI projects takes about 300–950 hours: roughly 11,000–37,500 at 40 per hour.',
    'Chatbots and RAG assistants sit at the lower end; custom ML and computer vision from raw data at the upper end.',
    'Most hours go into data, search, interface and testing, not the model; budgets that leave out testing and management overrun.',
    'Unready data, integrations, extra platforms and speed raise the cost; one use case with a clear success metric keeps it small.',
  ],
  georgianSpeechAi: [
    'Georgian is harder for speech AI than English: less training data, long word forms, mixed-language speech and Latin-script typing.',
    'Several providers support Georgian, but only a test on your own recordings shows how each one performs for you.',
    'Measure task success, latency, cost and data location — not only word error rate.',
    'Design for mistakes: confirm key details, offer buttons as a fallback, add custom vocabulary and keep a route to a person.',
  ],
  ragVsFineTuning: [
    'Fine-tuning changes how a model behaves; RAG gives it the right facts at question time and shows the sources.',
    'For answering questions from company documents, start with RAG — it stays current and can cite and respect permissions.',
    'Accuracy depends mostly on retrieval, honest “I don’t know” answers and a test set of real questions.',
    'Start with one document collection and one team, measure, then expand.',
  ],
  aiTeamGeorgia: [
    'Tbilisi is 2–3 hours ahead of Central Europe, so the whole European working day overlaps.',
    'Georgia has no EU adequacy decision: use a DPA with Standard Contractual Clauses and keep personal data in the EU where possible.',
    'The EU AI Act applies to systems used in the EU; plan risk classification, oversight and documentation early.',
    'Agree IP ownership, NDA and pricing in writing, check shipped work, and start with a small paid pilot.',
  ],
  aiChatbots: [
    'Large language models turned chatbots from keyword decision trees into assistants that understand intent, context and several languages.',
    'The most valuable assistants are connected to order systems, CRM and knowledge bases, so they resolve requests instead of reciting the FAQ.',
    'Good assistants answer only from verified company content and hand over to a person — with the full conversation — when stakes or emotions rise.',
    'Adopt incrementally: one high-volume, low-risk use case first, measured honestly, then widen the scope as trust grows.',
  ],
  aiDepressive: [
    'Wearable sleep, activity and heart-rate data can help clinicians follow depression care between visits — as support, never as a diagnosis.',
    'Compare each patient with their own baseline, not a population average, and describe changes in plain words rather than “risk scores”.',
    'Consent must be specific and revisable, data collection minimal, and GDPR or HIPAA planned from the first sprint.',
    'Tune alerts with clinicians to avoid alarm fatigue, test for bias across groups, and validate with clinical partners before claiming usefulness.',
  ],
  aiInArchitect: [
    'AI helps across a project in small jobs: early massing options, quick mood images, converting old drawings, rule checks and first drafts.',
    'AI images show atmosphere, not buildings — treat them as conversation starters, not checked designs.',
    'Tools like ArchiML turn 2D drawings into BIM models, but the result is a starting point that someone who knows the building must review.',
    'Design judgement and professional responsibility stay with the architect; start with one repetitive, easy-to-check task.',
  ],
  computerVision: [
    'Computer vision learns visual patterns from labelled examples and now matches human accuracy on many narrow tasks.',
    'Proven uses include quality inspection, document understanding, and medical imaging as a second pair of eyes.',
    'Pre-trained models mean a focused pilot often needs only hundreds of labelled examples and six to eight weeks.',
    'Real value comes from the details around the model: consistent images, handling uncertainty and wiring results into existing workflows.',
  ],
  dataPrivacy: [
    'Privacy in AI is an engineering discipline, and it costs far less when built in early than when retrofitted.',
    'Key techniques: data minimisation, federated learning, differential privacy and synthetic data for development.',
    'GDPR sets the legal baseline, but trust — how users see their data treated — is the real competitive advantage.',
    'Know your data lineage: what was collected, under which consent, which models used it, and how to remove it on request.',
  ],
  digitalHealth: [
    'Online search and booking for clinics and labs cut call-centre load and no-shows and remove the phone queue for patients.',
    'Medical tourism platforms combine specialist directories, verified credentials, transparent pricing and online consultations.',
    'Machine learning adds specialist matching, triage and translation, while health data demands security designed in from day one.',
    'Next come telemedicine and wearables — the patient journey now starts online.',
  ],
  genAi: [
    'Generative AI delivers when it is connected to real documents, data and workflows, with a person deciding what ships.',
    'The strongest uses are document drafting and summarising, knowledge access with cited sources, and supervised coding help.',
    'Common failures: chatbots without company data, unreviewed published content, and measuring nothing.',
    'Keep figures, legal commitments and medical decisions in systems built for correctness; start with one text-heavy workflow and measure for a quarter.',
  ],
  mlEcommerce: [
    'Recommendations and semantic search turn browsing data into sales and reduce empty search results.',
    'Demand forecasting and dynamic pricing improve purchasing, reduce stock-outs and free capital from slow inventory.',
    'Machine learning also scores payments for fraud, filters fake reviews and powers support chatbots.',
    'Start with one focused use case — usually recommendations or search — measure the change, then expand.',
  ],
  mlOutRails: [
    'Models rarely fail loudly: they keep returning confident answers while their inputs change and accuracy slips.',
    'Common causes are changed upstream data, a world that drifts away from the training data, rare inputs, feedback loops and over-reliance.',
    'Name an owner, document the intended use, and log, monitor and compare predictions with real outcomes — with a fallback when checks fail.',
    'Keep humans on high-stakes and low-confidence cases, release retrained models like software, and keep rollback ready.',
  ],
  mlRoadmap: [
    'Most ML projects fail because of vague goals, unready data or no path into daily work — not because of the model.',
    'Frame one measurable business question, audit the data early and build a simple baseline first.',
    'Integrate predictions into the tools people already use and pilot with real users before scaling.',
    'Monitor and retrain in production; a focused three-to-four-month first project builds the habits for the next ones.',
  ],
  mlrolehealth: [
    'Machine learning already works in imaging, operations such as no-shows and staffing, and paperwork like draft notes — with clinicians deciding.',
    'Autonomous diagnosis, individual treatment choices and models trained on very different populations are not ready yet.',
    'Hidden costs include messy records, integration with PACS or EHR, medical-device rules and monitoring after launch.',
    'Start with a frequent, narrow, measurable task, audit the data first, give it a clinical owner and run it in shadow mode.',
  ],
  moralRiskHealth: [
    'Adopting AI in a clinic moves risk rather than removing it, and patients still expect reasonable care.',
    'The goal is to make AI errors visible, catchable and traceable, because they are quiet and patterned.',
    'Six steps: a narrow stated job, local testing, real clinician oversight, documentation, data protection with a careful contract, and an incident plan.',
    'This is general information, not legal advice — check medical-device status and local law before rolling out.',
  ],
}

export default articleSummaries
